import React, { useEffect, useMemo, useState } from 'react'
import SearchBar from 'pages/pga-coach/students/search-bar'
import { makeStyles } from '@material-ui/styles'
import AcademyCoachRow from './academy-coach-row'
import EnhancedTable from 'components/tables/enhanced-table'
import TablePagination from 'components/pagination/table-pagination'
import PeopleOutlineIcon from 'components/material-symbol/icons/PeopleOutline'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import SectionEmptyState from 'components/section-empty-state'
import ExternalLinkButton from 'components/external-link-button'
import { Box, TableContainer, Typography } from '@material-ui/core'
import { useIsTablet } from 'lib/hooks'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'
import { PGA_COM_HOST } from 'env'
import {
  academyLink,
  matchesCoachSearchTerm,
  sortByCoachEmail,
  sortByCoachName,
  sortByCoachRole,
} from './utils'

const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
      padding: theme.spacing(3, 1, 2),
    },
  },
  academyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  academyName: {
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  searchField: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 360,
    width: '100%',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginLeft: 'auto',
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  listEmptyStateContent: {
    padding: theme.spacing(4, 2),
  },
}))

export default function AcademyCoachesListView({ academy }) {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const rowsPerPage = 25
  const employments = useMemo(() => academy?.employments || [], [
    academy?.employments,
  ])

  const filteredEmployments = useMemo(
    () =>
      employments.filter(employment =>
        matchesCoachSearchTerm(employment, searchTerm),
      ),
    [employments, searchTerm],
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet ? '45%' : '32%',
      mobile: true,
      sortable: true,
      sortComparator: sortByCoachName,
    },
    {
      id: 'role',
      label: 'Role',
      align: 'left',
      width: isTablet ? '25%' : '16%',
      mobile: true,
      sortable: true,
      sortComparator: sortByCoachRole,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'email',
      label: 'Email',
      align: 'left',
      width: isTablet ? '0%' : '32%',
      mobile: false,
      sortable: true,
      sortComparator: sortByCoachEmail,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: isTablet ? '30%' : '20%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredEmployments.length || 0) / rowsPerPage) || 1

  const rows = filteredEmployments.map(employment => (
    <AcademyCoachRow key={employment.id} employment={employment} />
  ))

  const path = academyLink(academy)

  return (
    <TableContainer className={classes.containerStyles}>
      <Box className={classes.academyHeader}>
        <Typography className={classes.academyName} component="h2">
          {academy.name}
        </Typography>
        <ExternalLinkButton color="primary" href={`${PGA_COM_HOST}/${path}`}>
          View Academy Page&nbsp;
          <OpenInNewOutlinedIcon fontSize="small" color="primary" />
        </ExternalLinkButton>
      </Box>
      <Box className={classes.tableShell}>
        <Box className={classes.toolbar}>
          <Box className={classes.searchGroup}>
            <SearchBar
              className={classes.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Coaches"
              data-testid="academy-coaches-search"
              style={{ margin: 0 }}
            />
          </Box>
          <Box className={classes.toolbarActions}>
            {filteredEmployments.length > 0 && (
              <TablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredEmployments.length}
                onPageChange={setPage}
                alwaysShowCount
              />
            )}
          </Box>
        </Box>
        <Box className={classes.tableContent}>
          <EnhancedTable
            rows={rows}
            columns={columns}
            label="Coaches"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="56px"
            initialOrderBy="name"
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={classes.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={PeopleOutlineIcon}
                  title="Coaches"
                  caption={
                    employments.length === 0
                      ? 'Your academy coaches will appear here.'
                      : 'No matching coaches.'
                  }
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}
